// ============================================================
// FILE: src/components/Navbar.jsx  →  TOP MENU BAR
// The sticky glass navigation: logo, menu links, dropdown
// mega-menus, dark/light toggle and "Get a Quote" button.
// ============================================================

import { useEffect, useState } from "react";
import { Link, NavLink, useLocation, useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { ChevronDown, Menu, Moon, Sun, X, ArrowRight, Download } from "lucide-react";
import { useTheme } from "./ThemeProvider";
import { PRODUCT_CATEGORIES, SERVICES } from "../data/site";
import { CATALOGUE_URL } from "../lib/api";

const NAV_LINKS = [
  { to: "/about", label: "About" },
  { to: "/industries", label: "Industries" },
  { to: "/countries", label: "Countries" },
  { to: "/insights", label: "Insights" },
  { to: "/contact", label: "Contact" },
];

export default function Navbar() {
  const { theme, toggle } = useTheme();
  const [scrolled, setScrolled] = useState(false);
  const [openMenu, setOpenMenu] = useState(null);
  const [mobileOpen, setMobileOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    setMobileOpen(false);
    setOpenMenu(null);
  }, [location.pathname]);

  const linkCls = ({ isActive }) =>
    `text-sm font-medium transition-colors duration-300 hover:text-teal-500 dark:hover:text-teal-400 ${
      isActive ? "text-teal-600 dark:text-teal-400" : "text-foreground/80"
    }`;

  return (
    <header
      data-testid="navbar"
      className={`fixed inset-x-0 top-0 z-[100] transition-[background-color,border-color,backdrop-filter] duration-500 ${
        scrolled
          ? "border-b border-slate-900/15 bg-white/95 shadow-[0_8px_40px_-16px_rgba(2,6,23,0.4)] backdrop-blur-2xl dark:border-white/15 dark:bg-slate-950/95"
          : "border-b border-transparent"
      }`}
    >
      <nav className="container-x flex h-24 items-center justify-between gap-4 lg:h-28">
        <Link to="/" className="flex items-center gap-3" data-testid="nav-logo" aria-label="Evolve Trade & Logistics — go to homepage">
          <img src="/logo.png" alt="Evolve Trade & Logistics" className="h-[4.5rem] w-auto object-contain drop-shadow-[0_2px_8px_rgba(20,184,166,0.35)] lg:h-24" />
        </Link>

        <div className="hidden items-center gap-7 lg:flex">
          <NavLink to="/about" className={linkCls} data-testid="nav-about">About</NavLink>

          <div className="relative" onMouseEnter={() => setOpenMenu("products")} onMouseLeave={() => setOpenMenu(null)}>
            <NavLink to="/products" className={linkCls} data-testid="nav-products">
              <span className="flex items-center gap-1">Products <ChevronDown className={`h-3.5 w-3.5 transition-transform duration-300 ${openMenu === "products" ? "rotate-180" : ""}`} /></span>
            </NavLink>
            <AnimatePresence>
              {openMenu === "products" && (
                <motion.div
                  initial={{ opacity: 0, y: 12, scale: 0.98 }}
                  animate={{ opacity: 1, y: 0, scale: 1 }}
                  exit={{ opacity: 0, y: 8, scale: 0.98 }}
                  transition={{ duration: 0.25, ease: [0.22, 1, 0.36, 1] }}
                  className="absolute left-1/2 top-full mt-3 w-[560px] -translate-x-1/2 rounded-2xl border border-slate-900/15 bg-white/95 p-6 shadow-2xl backdrop-blur-2xl dark:border-white/15 dark:bg-slate-950/95"
                  data-testid="products-mega-menu"
                >
                  <p className="section-label mb-4">Product Categories</p>
                  <div className="grid grid-cols-2 gap-1">
                    {PRODUCT_CATEGORIES.map((c) => (
                      <button
                        key={c}
                        onClick={() => navigate(`/products?category=${encodeURIComponent(c)}`)}
                        className="group flex items-center justify-between rounded-lg px-3 py-2.5 text-left text-sm text-foreground/80 transition-colors duration-200 hover:bg-teal-500/10 hover:text-teal-600 dark:hover:text-teal-400"
                        data-testid={`mega-cat-${c.toLowerCase().replace(/\s+/g, "-")}`}
                      >
                        {c}
                        <ArrowRight className="h-3.5 w-3.5 opacity-0 transition-opacity duration-200 group-hover:opacity-100" />
                      </button>
                    ))}
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
          </div>

          <div className="relative" onMouseEnter={() => setOpenMenu("services")} onMouseLeave={() => setOpenMenu(null)}>
            <NavLink to="/services" className={linkCls} data-testid="nav-services">
              <span className="flex items-center gap-1">Services <ChevronDown className={`h-3.5 w-3.5 transition-transform duration-300 ${openMenu === "services" ? "rotate-180" : ""}`} /></span>
            </NavLink>
            <AnimatePresence>
              {openMenu === "services" && (
                <motion.div
                  initial={{ opacity: 0, y: 12, scale: 0.98 }}
                  animate={{ opacity: 1, y: 0, scale: 1 }}
                  exit={{ opacity: 0, y: 8, scale: 0.98 }}
                  transition={{ duration: 0.25, ease: [0.22, 1, 0.36, 1] }}
                  className="absolute left-1/2 top-full mt-3 w-[620px] -translate-x-1/2 rounded-2xl border border-slate-900/15 bg-white/95 p-6 shadow-2xl backdrop-blur-2xl dark:border-white/15 dark:bg-slate-950/95"
                  data-testid="services-mega-menu"
                >
                  <p className="section-label mb-4">Trade Services</p>
                  <div className="grid grid-cols-2 gap-1">
                    {SERVICES.map((s) => (
                      <Link
                        key={s.title}
                        to="/services"
                        className="group flex items-center gap-3 rounded-lg px-3 py-2.5 transition-colors duration-200 hover:bg-teal-500/10"
                        data-testid={`mega-service-${s.title.toLowerCase().replace(/\s+/g, "-")}`}
                      >
                        <s.icon className="h-4 w-4 shrink-0 text-teal-500" />
                        <span className="text-sm text-foreground/80 group-hover:text-teal-600 dark:group-hover:text-teal-400">{s.title}</span>
                      </Link>
                    ))}
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
          </div>

          {NAV_LINKS.slice(1).map((l) => (
            <NavLink key={l.to} to={l.to} className={linkCls} data-testid={`nav-${l.label.toLowerCase()}`}>
              {l.label}
            </NavLink>
          ))}
        </div>

        <div className="flex items-center gap-3">
          <button
            onClick={toggle}
            aria-label="Toggle theme"
            data-testid="theme-toggle"
            className="glass flex h-10 w-10 items-center justify-center rounded-full transition-transform duration-300 hover:scale-110"
          >
            {theme === "dark" ? <Sun className="h-4 w-4 text-teal-400" /> : <Moon className="h-4 w-4 text-blue-700" />}
          </button>
          <a href={CATALOGUE_URL} target="_blank" rel="noreferrer" className="btn-secondary hidden !px-5 !py-2.5 lg:inline-flex" data-testid="nav-catalogue">
            <Download className="h-4 w-4" /> Catalogue
          </a>
          <Link to="/contact" className="btn-primary hidden !px-6 !py-2.5 lg:inline-flex" data-testid="nav-cta">
            Get a Quote
          </Link>
          <button
            className="glass flex h-10 w-10 items-center justify-center rounded-full lg:hidden"
            onClick={() => setMobileOpen(!mobileOpen)}
            aria-label="Menu"
            data-testid="mobile-menu-toggle"
          >
            {mobileOpen ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
          </button>
        </div>
      </nav>

      <AnimatePresence>
        {mobileOpen && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
            className="overflow-hidden border-b border-slate-900/15 bg-white/95 backdrop-blur-2xl dark:border-white/15 dark:bg-slate-950/95 lg:hidden"
            data-testid="mobile-menu"
          >
            <div className="container-x flex flex-col gap-1 py-6">
              {[{ to: "/about", label: "About" }, { to: "/products", label: "Products" }, { to: "/services", label: "Services" }, ...NAV_LINKS.slice(1)].map((l) => (
                <NavLink
                  key={l.to}
                  to={l.to}
                  className="rounded-lg px-3 py-3 text-base font-medium text-foreground/85 transition-colors hover:bg-teal-500/10 hover:text-teal-500"
                  data-testid={`mobile-nav-${l.label.toLowerCase()}`}
                >
                  {l.label}
                </NavLink>
              ))}
              <a href={CATALOGUE_URL} target="_blank" rel="noreferrer" className="btn-secondary mt-3" data-testid="mobile-nav-catalogue">
                <Download className="h-4 w-4" /> Download Catalogue
              </a>
              <Link to="/contact" className="btn-primary mt-3" data-testid="mobile-nav-cta">Get a Quote</Link>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
}
